import { useState } from "react";
import { HelpTip } from "@/components/help-tip";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/empty-state";
import { TableSkeleton } from "@/components/loading-skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { TrendingUp, Search, Trash2, Plus } from "lucide-react";
import { TutorialTip } from "@/components/TutorialTip";
import { usePageTour } from "@/components/ProductTour";
import { getCostCategories, type LearnedAdjustment } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { formatMoney } from "@/lib/utils";

interface AdjustmentWithClient extends LearnedAdjustment {
  clientName?: string;
}

const addAdjustmentSchema = z.object({
  category: z.string().min(1, "Category is required"),
  percent: z.string().refine((val) => {
    const num = Number(val);
    return !isNaN(num) && num >= -100 && num <= 500;
  }, { message: "Must be a number between -100 and 500" }),
  reason: z.string().optional(),
});

type AddAdjustmentForm = z.infer<typeof addAdjustmentSchema>;

export default function AdjustmentsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const { toast } = useToast();
  usePageTour("adjustments");

  const { data: adjustments, isLoading } = useQuery<AdjustmentWithClient[]>({
    queryKey: ["/api/adjustments"],
  });

  const form = useForm<AddAdjustmentForm>({
    resolver: zodResolver(addAdjustmentSchema),
    defaultValues: { category: "all", percent: "", reason: "" },
  });

  const addMutation = useMutation({
    mutationFn: (data: AddAdjustmentForm) =>
      apiRequest("POST", "/api/adjustments/learn", {
        percent: data.percent,
        reason: data.reason || "",
        category: data.category,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/adjustments"] });
      toast({ title: "Adjustment added" });
      setAddOpen(false);
      form.reset({ category: "all", percent: "", reason: "" });
    },
    onError: () => {
      toast({ title: "Failed to add adjustment", variant: "destructive" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiRequest("PATCH", `/api/adjustments/${id}`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/adjustments"] });
      toast({ title: "Adjustment updated" });
    },
    onError: () => {
      toast({ title: "Failed to update adjustment", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/adjustments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/adjustments"] });
      toast({ title: "Adjustment deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete adjustment", variant: "destructive" });
    },
  });

  const filteredAdjustments = adjustments?.filter((adj) =>
    adj.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    adj.reason?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    adj.clientName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const { user } = useAuth();
  const COST_CATEGORIES = getCostCategories(user?.market);

  const getCategoryLabel = (value: string) => {
    if (value === "all") return "All categories";
    return COST_CATEGORIES.find(c => c.value === value)?.label || value;
  };

  const formatAdjustment = (adj: LearnedAdjustment) => {
    const value = Number(adj.adjustmentValue);
    if (adj.adjustmentType === "multiplier") {
      const percent = (value - 1) * 100;
      return `${percent >= 0 ? "+" : ""}${percent.toFixed(1)}%`;
    }
    return `${value >= 0 ? "+" : ""}${formatMoney(value, user?.market)}`;
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2 flex-wrap" data-testid="text-adjustments-title">
            Learned Adjustments
            <HelpTip testId="button-help-adjustments" content="When you adjust prices on a quote and click 'Learn This Price', the system remembers your changes. Future quotes with similar items will automatically use your learned pricing, saving you time on repeat adjustments." />
          </h1>
          <p className="text-muted-foreground">
            View and manage price adjustments learned from your quote modifications
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="button-add-adjustment" className="shrink-0">
          <Plus className="h-4 w-4 mr-2" />
          Add Adjustment
        </Button>
      </div>

      <TutorialTip id="adjustments-intro" title="How Learned Adjustments Work">
        Each time you edit a price in a quote, that change is recorded here. The tool uses these records to calibrate future Auto Quotes to your real pricing patterns. You can also click <strong>Add Adjustment</strong> to manually set uplifts or discounts for specific trades or project types.
      </TutorialTip>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search adjustments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="input-search-adjustments"
        />
      </div>

      {/* Adjustments Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={5} />
            </div>
          ) : !filteredAdjustments || filteredAdjustments.length === 0 ? (
            <EmptyState
              icon={TrendingUp}
              title="No learned adjustments yet"
              description={searchQuery
                ? "Try adjusting your search"
                : "Adjustments will appear here as you modify prices on quotes. The system learns from your changes to improve future estimates."}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Adjustment</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Filters</TableHead>
                  <TableHead className="text-center">Confidence</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAdjustments.map((adj) => (
                  <TableRow key={adj.id} className={!adj.isActive ? "opacity-50" : ""} data-testid={`row-adjustment-${adj.id}`}>
                    <TableCell>
                      <Badge variant="secondary" className="font-normal">
                        {getCategoryLabel(adj.category)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className={`font-medium ${Number(adj.adjustmentValue) >= 1 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                        {formatAdjustment(adj)}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <span className="text-sm text-muted-foreground truncate block">
                        {adj.reason || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {adj.triggerProjectType && (
                          <Badge variant="outline" className="text-xs font-normal">
                            {adj.triggerProjectType.replace(/_/g, " ")}
                          </Badge>
                        )}
                        {adj.triggerClientId && adj.clientName && (
                          <Badge variant="outline" className="text-xs font-normal">
                            {adj.clientName}
                          </Badge>
                        )}
                        {adj.triggerRegion && (
                          <Badge variant="outline" className="text-xs font-normal">
                            {adj.triggerRegion}
                          </Badge>
                        )}
                        {!adj.triggerProjectType && !adj.triggerClientId && !adj.triggerRegion && (
                          <span className="text-xs text-muted-foreground">All quotes</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className={`w-2 h-2 rounded-full ${
                              i < (adj.confidenceScore || 1)
                                ? "bg-primary"
                                : "bg-muted"
                            }`}
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={adj.isActive ?? true}
                        onCheckedChange={(checked) => toggleMutation.mutate({ id: adj.id, isActive: checked })}
                        data-testid={`switch-active-${adj.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteMutation.mutate(adj.id)}
                        data-testid={`button-delete-${adj.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Adjustment Dialog */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) form.reset({ category: "all", percent: "", reason: "" }); }}>
        <DialogContent className="sm:max-w-md" data-testid="modal-add-adjustment">
          <DialogHeader>
            <DialogTitle>Add Adjustment</DialogTitle>
            <DialogDescription>Manually set a price adjustment for a category. This will be applied when using Learned or Custom pricing mode.</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit((data) => addMutation.mutate(data))} className="space-y-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-category">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="all">All categories</SelectItem>
                        {COST_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="percent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Adjustment (%)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        placeholder="e.g. 10 or -5"
                        data-testid="input-percent"
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">Positive to increase prices, negative to reduce. E.g. +10 adds 10% to all items in this category.</p>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. Labour always runs higher in this area"
                        data-testid="input-reason"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAddOpen(false)} data-testid="button-cancel-adjustment">
                  Cancel
                </Button>
                <Button type="submit" disabled={addMutation.isPending} data-testid="button-save-adjustment">
                  {addMutation.isPending ? "Saving..." : "Save Adjustment"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
